import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import { Controller, useForm } from 'react-hook-form';
import { BackrsFormTextField } from '../BackrsForm/BackrsFormTextField';
var RESET_STRINGS = appStrings.modules.login.resetPassword;
var MIN_PASSWORD_LENGTH = 8;
var defaultValues = {
    password: '',
    confirmPassword: '',
};
export var ResetPassword = function (_a) {
    var _b;
    var onSubmit = _a.onSubmit, onBackToLogin = _a.onBackToLogin, isLoading = _a.isLoading;
    var _c = useState(false), isPasswordVisible = _c[0], setIsPasswordVisible = _c[1];
    var _d = useState(false), isConfirmVisible = _d[0], setIsConfirmVisible = _d[1];
    var _e = useForm({
        defaultValues: defaultValues,
        mode: 'onTouched',
    }), control = _e.control, handleSubmit = _e.handleSubmit, watch = _e.watch, getValues = _e.getValues;
    var meetsMinLength = ((_b = watch('password')) !== null && _b !== void 0 ? _b : '').length >= MIN_PASSWORD_LENGTH;
    var passwordField = {
        id: 'reset-password-input',
        name: 'password',
        placeholder: RESET_STRINGS.passwordPlaceholder,
        type: isPasswordVisible ? 'text' : 'password',
        autoComplete: 'new-password',
        autoFocus: true,
        icon: {
            type: 'RIGHT_ICON',
            rightIcon: isPasswordVisible ? 'EyeOff' : 'Eye',
            onRightIconClick: function () { return setIsPasswordVisible(function (prev) { return !prev; }); },
        },
    };
    var confirmPasswordField = {
        id: 'reset-confirm-password-input',
        name: 'confirmPassword',
        placeholder: RESET_STRINGS.confirmPasswordPlaceholder,
        type: isConfirmVisible ? 'text' : 'password',
        autoComplete: 'new-password',
        icon: {
            type: 'RIGHT_ICON',
            rightIcon: isConfirmVisible ? 'EyeOff' : 'Eye',
            onRightIconClick: function () { return setIsConfirmVisible(function (prev) { return !prev; }); },
        },
    };
    var submit = handleSubmit(function (data) { return onSubmit(data.password, data.confirmPassword); });
    return (_jsxs(Box, { sx: cardSx, children: [_jsxs(Box, { sx: headerSx, children: [_jsx(Box, { sx: featuredIconSx, children: _jsx(AppIcon, { iconName: "Lock01", diameter: "28px", color: "Colors/Text/text-primary" }) }), _jsxs(Box, { sx: textContainerSx, children: [_jsx(Typography, { variant: "display-xs-semibold", color: function (theme) { return theme.backrs.colors['Colors/Text/text-primary']; }, children: RESET_STRINGS.title }), _jsx(Typography, { variant: "text-md-regular", color: function (theme) { return theme.backrs.colors['Colors/Text/text-tertiary']; }, children: RESET_STRINGS.subtitle })] })] }), _jsxs(Box, { component: "form", onSubmit: submit, noValidate: true, sx: formSx, children: [_jsxs(Box, { sx: fieldsSx, children: [_jsxs(Box, { sx: fieldGroupSx, children: [_jsx(Typography, { variant: "text-sm-medium", sx: fieldLabelSx, children: RESET_STRINGS.passwordPlaceholder }), _jsx(Controller, { name: "password", control: control, rules: {
                                            required: RESET_STRINGS.error.passwordRequired,
                                            minLength: { value: MIN_PASSWORD_LENGTH, message: RESET_STRINGS.error.minLength },
                                        }, render: function (_a) {
                                            var field = _a.field, error = _a.fieldState.error;
                                            return (_jsx(BackrsFormTextField, { field: passwordField, groupedFields: {}, index: 0, controllerField: field, error: error === null || error === void 0 ? void 0 : error.message }));
                                        } })] }), _jsxs(Box, { sx: fieldGroupSx, children: [_jsx(Typography, { variant: "text-sm-medium", sx: fieldLabelSx, children: RESET_STRINGS.confirmPasswordPlaceholder }), _jsx(Controller, { name: "confirmPassword", control: control, rules: {
                                            required: RESET_STRINGS.error.confirmRequired,
                                            validate: function (value) {
                                                return value === getValues('password') || RESET_STRINGS.error.mustMatch;
                                            },
                                        }, render: function (_a) {
                                            var field = _a.field, error = _a.fieldState.error;
                                            return (_jsx(BackrsFormTextField, { field: confirmPasswordField, groupedFields: {}, index: 0, controllerField: field, error: error === null || error === void 0 ? void 0 : error.message }));
                                        } })] }), _jsxs(Box, { sx: checkRowSx, children: [_jsx(Box, { sx: checkCircleSx(meetsMinLength), children: _jsx(AppIcon, { iconName: "Check", diameter: "12px", color: "Colors/Base/White" }) }), _jsx(Typography, { variant: "text-sm-regular", color: function (theme) { return theme.backrs.colors['Colors/Text/text-tertiary']; }, children: RESET_STRINGS.minLengthCheck })] })] }), _jsx(Button, { type: "submit", fullWidth: true, hierarchy: "Primary", size: "lg", id: "reset-password-submit-button", "data-ph-capture-attribute-name": "reset-password-submit-button", disabled: isLoading, icon: isLoading ? 'Leading' : 'Default', endIcon: isLoading ? (_jsx(Box, { sx: loadingSpinnerSx, children: _jsx(Lottie, { animationData: lottieAnimations.dotsDotting, loop: true }) })) : undefined, children: RESET_STRINGS.submitButton })] }), _jsxs(ButtonBase, { id: "reset-password-back-to-login-button", sx: backToLoginSx, onClick: onBackToLogin, disableRipple: true, children: [_jsx(AppIcon, { iconName: "ArrowLeft", diameter: "20px", color: "Colors/Text/text-tertiary" }), _jsx(Typography, { variant: "text-sm-semibold", sx: backToLoginLabelSx, children: RESET_STRINGS.backToLogin })] })] }));
};
var cardSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: backrs.spacing['spacing-4xl'],
        width: '100%',
        maxWidth: '440px',
        boxSizing: 'border-box',
        backgroundColor: {
            xs: backrs.colors['Beige/50'],
            sm: backrs.colors['Colors/Background/bg-primary'],
        },
        border: {
            xs: 'none',
            sm: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        },
        borderRadius: {
            xs: 0,
            sm: backrs.radius['radius-lg'],
        },
        px: {
            xs: 0,
            sm: backrs.spacing['spacing-4xl'],
        },
        py: {
            xs: backrs.spacing['spacing-2xl'],
            sm: backrs.spacing['spacing-4xl'],
        },
    });
};
var headerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: backrs.spacing['spacing-3xl'],
        width: '100%',
    });
};
var featuredIconSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '56px',
        height: '56px',
        backgroundColor: backrs.colors['Colors/Background/bg-primary'],
        border: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        borderRadius: backrs.radius['radius-lg'],
        boxShadow: "2px 2px 0px 0px ".concat(backrs.colors['Colors/Base/Black']),
    });
};
var textContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: backrs.spacing['spacing-md'],
        width: '100%',
    });
};
var formSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-3xl'],
        width: '100%',
    });
};
var fieldsSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-2xl'],
        width: '100%',
    });
};
var fieldGroupSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-sm'],
        width: '100%',
    });
};
var fieldLabelSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-secondary'],
    });
};
var checkRowSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: backrs.spacing['spacing-md'],
        width: '100%',
    });
};
var checkCircleSx = function (isValid) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '20px',
            height: '20px',
            borderRadius: backrs.radius['radius-full'],
            backgroundColor: isValid
                ? backrs.colors['Colors/Foreground/fg-success-primary']
                : backrs.colors['Colors/Foreground/fg-disabled-subtle'],
        });
    };
};
var loadingSpinnerSx = {
    width: 20,
    height: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};
var backToLoginSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: backrs.spacing['spacing-sm'],
    });
};
var backToLoginLabelSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Colors/Text/text-tertiary'],
    });
};
